import { useDayjsFormatter } from "@jmc/utils/hooks/useDayjsFormatter";
import { EligibleEvent } from "@redux/modules/eligibleEvents";
import { EventError, EventStatus, QuickEvent } from "@types";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useTranslation } from "react-i18next";
import yn from "yn";

dayjs.extend(localizedFormat);
dayjs.extend(isBetween);

const determineDate = (startTime: string, endTime: string): string => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    if (start.isSame(end, "day")) return useDayjsFormatter("LL", startTime);
    return `${useDayjsFormatter("LL", startTime)} - ${useDayjsFormatter("LL", endTime)}`;
};

const determineTime = (startTime: string, endTime: string): string => {
    const { t } = useTranslation();
    const twentyFourHourNotation = yn(process.env.GATSBY_24_HOUR_NOTATION);
    return `${t("From", { ns: "common" })} ${useDayjsFormatter(twentyFourHourNotation ? "HH:mm" : "LT", startTime)} ${t(
        "common:till",
    )} ${useDayjsFormatter(twentyFourHourNotation ? "HH:mm" : "LT", endTime)}`;
};

const getTimerString = (time: number): string => {
    if (time < 0 || isNaN(time)) return null;
    return `${Math.floor(time / 60 / 60 / 24)}d:${Math.floor((time / 60 / 60) % 24)}h:${((time / 60) % 60).toFixed(
        0,
    )}m`;
};

const getStatus = (quickEvent: QuickEvent): EventStatus => {
    const startTime = dayjs(quickEvent.startTime);
    const endTime = dayjs(quickEvent.endTime);
    const currentTime = dayjs();

    if (endTime.isBefore(currentTime)) {
        return EventStatus.LIVE_FINALISED;
    }
    if (currentTime.isBetween(startTime, endTime, "second", "[]")) {
        return EventStatus.LIVE_NOW;
    }
    if (startTime.isAfter(currentTime.add(30, "minutes"))) {
        return EventStatus.UPCOMING_LIVE;
    }
    return EventStatus.LIVE_SOON;
};

const isPublishedQuickEvent = (event: QuickEvent): boolean => {
    if (!event) return false;
    if (!event?.publishDate || !event?.unpublishDate) return false;
    const now = dayjs();
    const publishDate = dayjs(event.publishDate);
    const unpublishDate = dayjs(event.unpublishDate);
    return now.isAfter(publishDate) && now.isBefore(unpublishDate);
};

const canQuickEventBeDisplayed = (event: QuickEvent, eligibleEvents: EligibleEvent[]): number => {
    const isEligible = eligibleEvents?.find((eligibleEvent) => eligibleEvent.id === event.id);
    const isPublished = isPublishedQuickEvent(event);

    if (!isEligible) {
        return EventError.INELIGIBLE;
    }
    if (!isPublished) {
        return EventError.UNPUBLISHED;
    }
    return EventError.ALL_CLEAR;
};

export default {
    determineDate,
    determineTime,
    getTimerString,
    getStatus,
    isPublishedQuickEvent,
    canQuickEventBeDisplayed,
};
